<template>
  <div class="page item-card">
    <ErrorAlert :message="errorMessage" @clear-error="errorMessage = ''" />
    <SuccessAlert :message="successMessage" @clear-error="successMessage = ''" />

    <PageHeader :title="headerTitle" :subtitle="headerSubtitle" :icon="item ? typeIcon(item.equipmentType) : 'mdi-qrcode'">
      <template v-if="item && item.canView && (canEditItem || canSeeHistory)" #actions>
        <v-btn v-if="canSeeHistory" class="btn-ghost-light" @click="showHistory">
          <v-icon left>mdi-history</v-icon>Istoric
        </v-btn>
        <v-btn v-if="canEditItem" class="btn-leaf" :loading="editLoading" @click="editItem">
          <v-icon left>mdi-pencil-outline</v-icon>Editează
        </v-btn>
      </template>
    </PageHeader>

    <div class="page-body">
      <div v-if="loading" class="panel ic-loading">
        <v-progress-circular indeterminate color="primary" size="40" width="3" />
        <span>Se încarcă echipamentul…</span>
      </div>

      <section v-else-if="notFound || loadError" class="panel ic-panel ic-missing">
        <v-icon large class="ic-missing__icon">{{ notFound ? "mdi-qrcode-remove" : "mdi-alert-circle-outline" }}</v-icon>
        <h2 class="ic-missing__title">{{ notFound ? "Echipamentul nu a fost găsit" : "Echipamentul nu a putut fi încărcat" }}</h2>
        <p class="ic-missing__text">
          {{ notFound ? `Codul ${tag} nu aparține niciunui echipament din Suport.` : loadError }}
        </p>
        <div class="ic-call">
          <v-icon>mdi-phone-outline</v-icon>
          <span>Sunați la DTI, of. 369, tel. 52-388</span>
        </div>
        <v-btn v-if="loadError" outlined color="primary" class="mt-4" @click="load">
          <v-icon left>mdi-refresh</v-icon>Reîncearcă
        </v-btn>
      </section>

      <div v-else-if="item" class="ic-layout">
        <div class="ic-col">
          <section class="panel ic-panel">
            <div class="ic-hero">
              <span class="ic-hero__icon" aria-hidden="true"><v-icon>{{ typeIcon(item.equipmentType) }}</v-icon></span>
              <div class="ic-hero__text">
                <div class="ic-hero__type">{{ item.equipmentType || "Echipament" }}</div>
                <h2 class="ic-hero__model">{{ item.model || "—" }}</h2>
                <div class="ic-hero__chips">
                  <span class="ic-chip ic-chip--tag" title="Codul de pe eticheta QR">
                    <v-icon x-small>mdi-qrcode</v-icon>{{ item.tag }}
                  </span>
                  <span v-if="!item.isActive" class="ic-chip ic-chip--amber">
                    <v-icon x-small>mdi-timer-sand</v-icon>echipament nou, în așteptarea demersului
                  </span>
                  <span v-else-if="!item.isFunctional" class="ic-chip ic-chip--slate">
                    <v-icon x-small>mdi-power-plug-off-outline</v-icon>nu este activ
                  </span>
                </div>
              </div>
            </div>

            <v-btn
              v-if="item.canRequest"
              class="btn-green ic-cta"
              x-large
              block
              @click="fileRequest"
            >
              <v-icon left>mdi-file-document-edit-outline</v-icon>Creează demers
            </v-btn>
            <p v-else-if="item.canView && requestNote" class="ic-note">
              <v-icon small>mdi-information-outline</v-icon><span>{{ requestNote }}</span>
            </p>

            <dl class="ic-facts">
              <div class="ic-fact">
                <dt><v-icon small>mdi-identifier</v-icon>Seria</dt>
                <dd>{{ item.serial_number || "—" }}</dd>
              </div>
              <div v-if="item.canView" class="ic-fact">
                <dt><v-icon small>mdi-barcode</v-icon>Nr. inventar</dt>
                <dd>{{ item.registration_number || "—" }}</dd>
              </div>
              <div v-if="item.canView" class="ic-fact">
                <dt><v-icon small>mdi-account-outline</v-icon>Responsabil</dt>
                <dd>{{ item.responsible ? item.responsible.name : "—" }}</dd>
              </div>
              <div v-if="item.canView" class="ic-fact">
                <dt><v-icon small>mdi-domain</v-icon>Departament</dt>
                <dd>{{ item.department || item.ownerDepartment || "—" }}</dd>
              </div>
              <div class="ic-fact">
                <dt><v-icon small>mdi-map-marker-outline</v-icon>Locația</dt>
                <dd>{{ locationText }}</dd>
              </div>
              <div v-if="item.location_details" class="ic-fact">
                <dt><v-icon small>mdi-map-marker-radius-outline</v-icon>Detalii amplasare</dt>
                <dd>{{ item.location_details }}</dd>
              </div>
            </dl>

            <div v-if="item.canView && (item.place_file || hasLabelKey)" class="ic-photos">
              <figure v-if="item.place_file" class="ic-photo">
                <button type="button" class="ic-photo__open" aria-label="Deschide fotografia amplasării" @click="openPhoto('place')">
                  <AuthImage :file-id="item.place_file.id" :lazy="false" alt="Fotografia amplasării" />
                </button>
                <figcaption>Amplasare</figcaption>
              </figure>
              <figure v-if="hasLabelKey" class="ic-photo">
                <button
                  v-if="item.label_file"
                  type="button"
                  class="ic-photo__open"
                  aria-label="Deschide fotografia etichetei"
                  @click="openPhoto('label')"
                >
                  <AuthImage :file-id="item.label_file.id" :lazy="false" alt="Fotografia etichetei" />
                </button>
                <div v-else class="ic-photo__none">
                  <v-icon>mdi-image-off-outline</v-icon>
                  <span>Fără fotografia etichetei</span>
                </div>
                <figcaption><v-icon x-small>mdi-lock-outline</v-icon>Etichetă · doar DTI și responsabilul</figcaption>
              </figure>
            </div>
            <PhotoGallery ref="placeViewer" :files="item.place_file ? [item.place_file] : []" hide-strip :download-name="`amplasare-${item.tag}`" />
            <PhotoGallery ref="labelViewer" :files="item.label_file ? [item.label_file] : []" hide-strip :download-name="`eticheta-${item.tag}`" />

            <div v-if="!item.canView" class="ic-restricted">
              <v-icon>mdi-shield-lock-outline</v-icon>
              <div>
                <p class="ic-restricted__title">Nu aveți acces la detaliile acestui echipament.</p>
                <p class="ic-restricted__text">Dacă are o problemă, anunțați DTI:</p>
                <div class="ic-call">
                  <v-icon>mdi-phone-outline</v-icon>
                  <span>Sunați la DTI, of. 369, tel. 52-388</span>
                </div>
              </div>
            </div>
          </section>

          <section v-if="item.canView" class="panel ic-panel">
            <h2 class="ic-section-title">
              <v-icon small>mdi-file-document-multiple-outline</v-icon>Demersuri deschise
              <span class="ic-count">{{ openRequests.length }}</span>
            </h2>
            <div v-if="!openRequests.length" class="ic-empty">
              <v-icon small>mdi-check-circle-outline</v-icon>Niciun demers deschis pentru acest echipament.
            </div>
            <ul v-else class="ic-requests">
              <li v-for="r in openRequests" :key="r.id">
                <router-link class="ic-request" :to="{ path: '/history', query: { cerere: r.id } }">
                  <div class="ic-request__top">
                    <span class="ic-request__cause">{{ r.cause || "Demers" }}</span>
                    <v-icon small class="ic-request__go">mdi-chevron-right</v-icon>
                  </div>
                  <StatusChip :status="r.status" small />
                  <div class="ic-request__meta">
                    <template v-if="r.approach_number">Nr. {{ r.approach_number }} · </template>{{ formatDate(r.date) }}
                    <template v-if="r.user"> · {{ r.user }}</template>
                  </div>
                </router-link>
              </li>
            </ul>
          </section>
        </div>

        <div v-if="item.canView" class="ic-col">
          <section class="panel ic-panel">
            <h2 class="ic-section-title">
              <v-icon small>mdi-comment-multiple-outline</v-icon>Comentarii
              <span class="ic-count">{{ commentCount }}</span>
            </h2>
            <ItemComments :equipment-id="item.id" @count="commentCount = $event" />
          </section>
        </div>
      </div>
    </div>

    <DialogForm v-if="requestDialogUsed" ref="requestDialog" @error="errorMessage = $event" @success="onFiled" />
    <EditEquipmentPopup
      v-if="canEdit && editUsed"
      ref="editPopup"
      :equipment="editRow"
      @updateEquipmentData="load"
      @error="errorMessage = $event"
      @success="successMessage = $event"
    />
    <EquipmentHistoryDialog v-if="canSeeHistory && historyUsed" ref="historyDialog" />
  </div>
</template>

<script>
// The item behind a QR sticker (/e/<tag>), for every logged-in role. Who may see the item gets its details,
// photos, open demersuri and comments; anyone else only what the sticker shows + the room + DTI's phone.
import * as api from "./api";
import { typeIcon } from "./pickers";
import PageHeader from "@/components/layout/PageHeader.vue";
import StatusChip from "@/components/elements/StatusChip.vue";
import AuthImage from "@/components/files/AuthImage.vue";
import PhotoGallery from "@/components/files/PhotoGallery.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";
import ItemComments from "./ItemComments.vue";

// same groups as the server: EQUIPMENT_MANAGERS edit, EQUIPMENT_OVERVIEW_ROLES see the history
const EDITOR_ROLES = ["admin", "inginer", "inginer-pc", "dtisef"];
const HISTORY_ROLES = [...EDITOR_ROLES, "rector"];

export default {
  name: "ItemCardPage",
  components: {
    PageHeader,
    StatusChip,
    AuthImage,
    PhotoGallery,
    ErrorAlert,
    SuccessAlert,
    ItemComments,
    DialogForm: () => import("../requests/DialogForm.vue"),
    EditEquipmentPopup: () => import("./EditEquipmentPopup.vue"),
    EquipmentHistoryDialog: () => import("./EquipmentHistoryDialog.vue"),
  },
  props: {
    tag: { type: String, required: true },
  },
  data() {
    return {
      item: null,
      loading: true,
      notFound: false,
      loadError: "",
      commentCount: 0,
      errorMessage: "",
      successMessage: "",
      requestDialogUsed: false,
      editUsed: false,
      editLoading: false,
      editRow: null,
      historyUsed: false,
    };
  },
  computed: {
    role() {
      return (this.$store.getters.getUser || {}).role;
    },
    canEdit() {
      return EDITOR_ROLES.includes(this.role);
    },
    // a new item still waiting for its demer is edited from the demer (logged there), not here
    canEditItem() {
      return this.canEdit && !!this.item && this.item.isActive;
    },
    canSeeHistory() {
      return HISTORY_ROLES.includes(this.role);
    },
    // the card itself shows type + model big; the band only names the page
    headerTitle() {
      return "Fișa echipamentului";
    },
    headerSubtitle() {
      if (!this.item) return `Cod ${String(this.tag || "").toUpperCase()}`;
      const parts = [this.item.location && this.item.location.label, this.item.department].filter(Boolean);
      return parts.join(" · ") || `Cod ${this.item.tag}`;
    },
    locationText() {
      const l = this.item && this.item.location;
      if (!l) return "—";
      // "Corp 3, Et. 3, 373"
      return l.label || [l.block, l.floor, l.room].filter(Boolean).join(", ") || "—";
    },
    // the server leaves label_file out for who may not see the label photo
    hasLabelKey() {
      return !!this.item && this.item.canSeeLabel && Object.prototype.hasOwnProperty.call(this.item, "label_file");
    },
    openRequests() {
      return (this.item && this.item.openRequests) || [];
    },
    // why there is no „Creează demers” for someone who sees the item
    requestNote() {
      const it = this.item;
      if (!it || it.canRequest) return "";
      if (!it.isActive) {
        return this.canEdit
          ? "Echipamentul apare în Suport după ce demersul lui este îndeplinit. Până atunci datele lui se modifică din demers („Editează datele echipamentului”)."
          : "Echipamentul apare în Suport după ce demersul lui este îndeplinit.";
      }
      if (!it.isFunctional) return "Echipamentul nu este activ: pentru el nu se pot depune demersuri.";
      return "Demersurile pentru acest echipament le depune departamentul căruia îi aparține.";
    },
  },
  watch: {
    // a notification for another item opens its card on the same page; the canonical code load() puts in the
    // link is the card already shown
    tag(tag) {
      if (this.item && this.item.tag === tag) return;
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    typeIcon,
    async load() {
      const tag = this.tag;
      this.loading = !this.item || this.item.tag !== String(tag).toUpperCase();
      this.notFound = false;
      this.loadError = "";
      try {
        const data = await api.getItemByTag(tag);
        if (tag !== this.tag) return;
        this.item = data;
        // an old row's code or lower case in the link: show the canonical one
        if (data.tag && data.tag !== tag) this.$router.replace({ name: "itemCard", params: { tag: data.tag } }).catch(() => {});
      } catch (error) {
        if (tag !== this.tag) return;
        this.item = null;
        const status = error.response && error.response.status;
        if (status === 404) this.notFound = true;
        else this.loadError = (error.response && error.response.data && error.response.data.message) || "Verificați conexiunea și încercați din nou.";
      } finally {
        if (tag === this.tag) this.loading = false;
      }
    },
    openPhoto(which) {
      const viewer = which === "label" ? this.$refs.labelViewer : this.$refs.placeViewer;
      if (viewer) viewer.open(0);
    },
    async fileRequest() {
      this.requestDialogUsed = true;
      await this.waitForRef("requestDialog");
      this.$refs.requestDialog.openFor({
        id: this.item.id,
        equipmentTypeId: this.item.equipmentTypeId,
        model: this.item.model,
        requestDepartment: this.item.requestDepartment,
      });
    },
    onFiled(message) {
      this.successMessage = message || "Demersul a fost trimis";
      this.load();
    },
    // the edit dialog takes a row of the Echipamente table
    async editItem() {
      this.editLoading = true;
      try {
        const e = await api.getEquipment(this.item.id);
        const owner = e.userId;
        const location = e.locationId || {};
        this.editRow = {
          id: e.id,
          equipmentTypeId: e.equipmentTypeId ? e.equipmentTypeId.id : this.item.equipmentTypeId,
          equipmentType: e.equipmentTypeId ? e.equipmentTypeId.type : this.item.equipmentType,
          responsable_person: owner ? `${owner.first_name} ${owner.last_name}` : "",
          model: e.model,
          registration_number: e.registration_number,
          departmentId: e.departmentId || null,
          block: location.block,
          floor: location.floor,
          room: location.room,
          isFunctional: e.isFunctional,
          serial_number: e.serial_number,
          location_details: e.location_details,
          place_file: e.place_file || null,
          ...("label_file" in e ? { label_file: e.label_file } : {}),
        };
        this.editUsed = true;
        await this.waitForRef("editPopup");
        this.$refs.editPopup.show();
      } catch (error) {
        this.errorMessage = error;
      } finally {
        this.editLoading = false;
      }
    },
    async showHistory() {
      this.historyUsed = true;
      await this.waitForRef("historyDialog");
      this.$refs.historyDialog.show({
        id: this.item.id,
        equipmentType: this.item.equipmentType,
        model: this.item.model,
        registration_number: this.item.registration_number,
        responsable_person: this.item.responsible ? this.item.responsible.name : "",
      });
    },
    // lazily loaded dialogs: their chunk may take a moment
    async waitForRef(name) {
      for (let i = 0; i < 100 && !this.$refs[name]; i++) {
        await new Promise((resolve) => setTimeout(resolve, 30));
      }
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date)) return String(value);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
    },
  },
};
</script>

<style scoped>
.ic-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 48px 16px;
  color: var(--ink-3);
}
.ic-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}
@media (max-width: 960px) {
  .ic-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}
.ic-col {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}
.ic-panel {
  padding: 20px;
}
@media (max-width: 600px) {
  .ic-panel {
    padding: 16px 14px;
  }
}
.ic-hero {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}
.ic-hero__icon {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: var(--mint-50);
  border: 1px solid var(--leaf-200);
}
.ic-hero__icon .v-icon {
  color: var(--green) !important;
  font-size: 28px !important;
}
.ic-hero__text {
  min-width: 0;
}
.ic-hero__type {
  color: var(--ink-3);
  font-weight: 700;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.ic-hero__model {
  margin: 2px 0 0;
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.ic-hero__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.ic-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
}
.ic-chip--tag {
  background: var(--pine-900);
  color: var(--leaf);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  letter-spacing: 0.06em;
}
.ic-chip--tag .v-icon {
  color: var(--leaf) !important;
}
.ic-chip--amber {
  background: var(--amber-bg);
  color: var(--amber);
}
.ic-chip--amber .v-icon {
  color: var(--amber) !important;
}
.ic-chip--slate {
  background: var(--slate-bg);
  color: var(--slate);
}
.ic-chip--slate .v-icon {
  color: var(--slate) !important;
}
.v-application .ic-cta.v-btn {
  margin-top: 18px;
  height: 56px !important;
  font-size: 1.1rem;
}
.ic-note {
  display: flex;
  gap: 8px;
  margin: 16px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--mint-50);
  color: var(--ink-2);
  font-size: 0.95rem;
}
.ic-note .v-icon {
  margin-top: 2px;
  color: var(--green) !important;
}
.ic-facts {
  margin: 18px 0 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0 16px;
}
@media (max-width: 600px) {
  .ic-facts {
    grid-template-columns: minmax(0, 1fr);
  }
}
.ic-fact {
  padding: 10px 0;
  border-bottom: 1px solid var(--line-soft);
  min-width: 0;
}
.ic-fact dt {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--ink-3);
  font-size: 0.85rem;
  font-weight: 700;
}
.ic-fact dt .v-icon {
  color: var(--green) !important;
}
.ic-fact dd {
  margin: 2px 0 0 22px;
  font-size: 1.02rem;
  font-weight: 600;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.ic-photos {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}
.ic-photo {
  margin: 0;
  min-width: 0;
}
.ic-photo__open,
.ic-photo__none {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--line);
  background: var(--mint-50);
  padding: 0;
  cursor: zoom-in;
}
.ic-photo__open ::v-deep img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.ic-photo__none {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--ink-3);
  font-size: 0.85rem;
  cursor: default;
  text-align: center;
  padding: 8px;
}
.ic-photo figcaption {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  color: var(--ink-3);
  font-size: 0.85rem;
  font-weight: 600;
}
.ic-photo figcaption .v-icon {
  color: var(--amber) !important;
}
.ic-restricted {
  display: flex;
  gap: 12px;
  margin-top: 18px;
  padding: 14px;
  border-radius: 12px;
  background: var(--sky-bg);
  color: var(--ink-2);
}
.ic-restricted > .v-icon {
  color: var(--sky) !important;
  align-self: flex-start;
}
.ic-restricted__title {
  margin: 0;
  font-weight: 700;
  color: var(--ink);
}
.ic-restricted__text {
  margin: 4px 0 8px;
}
.ic-call {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 10px;
  background: var(--pine-900);
  color: #fff;
  font-weight: 700;
  font-size: 1.02rem;
}
.ic-call .v-icon {
  color: var(--leaf) !important;
}
.ic-missing {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 36px 20px;
}
.ic-missing__icon {
  color: var(--ink-3) !important;
}
.ic-missing__title {
  margin: 10px 0 4px;
  font-size: 1.35rem;
}
.ic-missing__text {
  margin: 0 0 16px;
  color: var(--ink-3);
}
.ic-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  font-size: 1.15rem;
}
.ic-section-title .v-icon {
  color: var(--green) !important;
}
.ic-count {
  font-family: var(--font-ui);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--ink-3);
}
.ic-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--mint-50);
  color: var(--ink-3);
}
.ic-empty .v-icon {
  color: var(--green) !important;
}
.ic-requests {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ic-request {
  display: block;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink) !important;
  text-decoration: none;
}
.ic-request:hover {
  border-color: var(--green);
  background: var(--mint-50);
}
.ic-request__top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.ic-request__cause {
  flex: 1;
  font-weight: 700;
}
.ic-request__go {
  color: var(--ink-3) !important;
}
.ic-request__meta {
  margin-top: 6px;
  color: var(--ink-3);
  font-size: 0.88rem;
}
</style>
